import {
  ColumnDataTypes,
  FilterOperators,
  FilterOptionTypes,
  type ColumnConfig,
  type FilterOperatorObject,
} from '@/components/DataTable';
import { AuditOperation } from '@kirin/api-client/contracts/audit-logs';
import {
  AUDIT_LOGS_FILTERABLE_FIELDS,
  AUDIT_LOGS_TIMESTAMP_OPERATORS,
  AUDIT_LOGS_RECORD_ID_OPERATORS,
  type AuditLog,
} from '@kirin/api-client/contracts/audit-logs-table';
import { RelativeTimeWithTooltip } from '@/components/shared/RelativeTimeWithTooltip';
import {
  ObjectTypeCell,
  OperationBadgeCell,
  ObjectIdCell,
  AuditLogUserCell,
  ChangedFieldsDisplay,
  AuditSourceCell,
} from './CellRenderers';
import {
  OperationFilterOption,
  ObjectTypeFilterOption,
  ChangesFilterOption,
  UserFilterOption,
} from './FilterComponents';
import {
  fetchObjectTypeFilterOptions,
  getObjectTypeFilterOptionsQueryKey,
  fetchAuditLogUserFilterOptions,
  getAuditLogUserFilterOptionsQueryKey,
  fetchChangedFieldsFilterOptions,
  getChangedFieldsFilterOptionsQueryKey,
} from './api';
import { AUDIT_LOGS_FILTER_URL_KEYS, auditScopeGroupId } from './utils';
import { TIMESTAMP_COLUMN_MIN_WIDTH_LONG_HEADER } from '@/components/shared/timestampColumnWidth';

const FIELDS = AUDIT_LOGS_FILTERABLE_FIELDS;

const DATE_OPERATOR_LABELS: Record<string, string> = {
  [FilterOperators.GREATER_THAN]: 'Is After',
  [FilterOperators.LESS_THAN]: 'Is Before',
  [FilterOperators.BETWEEN]: 'Is Between',
};

const RECORD_ID_OPERATOR_LABELS: Record<string, string> = {
  [FilterOperators.EQUALS]: 'Is',
  [FilterOperators.CONTAINS]: 'Contains',
  [FilterOperators.NOT_CONTAINS]: 'Does not contain',
  [FilterOperators.STARTS_WITH]: 'Starts with',
  [FilterOperators.ENDS_WITH]: 'Ends with',
  [FilterOperators.IS_EMPTY]: 'Is Empty',
  [FilterOperators.IS_NOT_EMPTY]: 'Is Not Empty',
};

const VALUE_LESS_OPERATORS: FilterOperatorObject[] = [
  { value: FilterOperators.IS_EMPTY, label: 'Is Empty' },
  { value: FilterOperators.IS_NOT_EMPTY, label: 'Is Not Empty' },
];

// Range operators (from the shipped contract for parity) plus value-less.
const AUDIT_LOG_DATE_OPERATORS: FilterOperatorObject[] = [
  ...AUDIT_LOGS_TIMESTAMP_OPERATORS.map(value => ({
    value,
    label: DATE_OPERATOR_LABELS[value],
  })),
  ...VALUE_LESS_OPERATORS,
];

// recordId value-less ops come from the contract set, labelled above.
const AUDIT_LOG_RECORD_ID_OPERATORS: FilterOperatorObject[] =
  AUDIT_LOGS_RECORD_ID_OPERATORS.map(value => ({
    value,
    label: RECORD_ID_OPERATOR_LABELS[value],
  }));

const ENUM_OPERATORS: FilterOperatorObject[] = [
  { value: FilterOperators.INCLUDES, label: 'Includes' },
  { value: FilterOperators.EXCLUDES, label: 'Excludes' },
  ...VALUE_LESS_OPERATORS,
];

// Alphabetical; value === label === the uppercase enum, matching the cell badge.
const OPERATION_OPTIONS = [
  { value: AuditOperation.Created, label: AuditOperation.Created },
  { value: AuditOperation.Deleted, label: AuditOperation.Deleted },
  { value: AuditOperation.Login, label: AuditOperation.Login },
  { value: AuditOperation.Logout, label: AuditOperation.Logout },
  { value: AuditOperation.Revoked, label: AuditOperation.Revoked },
  { value: AuditOperation.Updated, label: AuditOperation.Updated },
  { value: AuditOperation.Viewed, label: AuditOperation.Viewed },
];

/** The six audit-log columns with cells, sort, and per-column filters. */
export function createAuditLogsColumns(
  organizationId: string,
  /** KIR-4701: security group names by id, for group-scoped changes. */
  groupNames?: ReadonlyMap<string, string>
): ColumnConfig<AuditLog>[] {
  return [
    {
      id: FIELDS.TIMESTAMP,
      header: 'Timestamp',
      accessor: 'timestamp',
      timestamp: true,
      // KIR-5639: the one-line header outgrows the relative width.
      minWidth: TIMESTAMP_COLUMN_MIN_WIDTH_LONG_HEADER,
      sortable: true,
      sortField: FIELDS.TIMESTAMP,
      sortKey: AUDIT_LOGS_FILTER_URL_KEYS[FIELDS.TIMESTAMP],
      renderCell: ({ row }) => <RelativeTimeWithTooltip date={row.timestamp} />,
      filterable: true,
      dataType: ColumnDataTypes.DATE,
      filterOperators: AUDIT_LOG_DATE_OPERATORS,
      statePersistence: {
        urlKey: AUDIT_LOGS_FILTER_URL_KEYS[FIELDS.TIMESTAMP],
      },
      filterOptions: {
        type: FilterOptionTypes.STATIC,
        options: [],
      },
    },
    {
      id: FIELDS.TABLE_NAME,
      header: 'Object Type',
      accessor: 'tableName',
      width: 220,
      sortable: true,
      sortField: FIELDS.TABLE_NAME,
      sortKey: AUDIT_LOGS_FILTER_URL_KEYS[FIELDS.TABLE_NAME],
      renderCell: ({ row }) => <ObjectTypeCell tableName={row.tableName} />,
      filterable: true,
      dataType: ColumnDataTypes.ENUM,
      filterOperators: ENUM_OPERATORS,
      statePersistence: {
        urlKey: AUDIT_LOGS_FILTER_URL_KEYS[FIELDS.TABLE_NAME],
      },
      filterOptions: {
        type: FilterOptionTypes.DYNAMIC,
        getQueryKey: params =>
          getObjectTypeFilterOptionsQueryKey(organizationId, params),
        queryFn: params => fetchObjectTypeFilterOptions(organizationId, params),
        queryConfig: { staleTime: 2 * 60 * 1000 },
        dropdown: { width: 280 },
        pagination: { mode: 'offset', limit: 20 },
        render: (option, isSelected) => (
          <ObjectTypeFilterOption
            label={option.label ?? option.value}
            isSelected={isSelected}
          />
        ),
      },
    },
    {
      id: FIELDS.OPERATION,
      header: 'Operation',
      accessor: 'operation',
      width: 150,
      sortable: true,
      sortField: FIELDS.OPERATION,
      sortKey: AUDIT_LOGS_FILTER_URL_KEYS[FIELDS.OPERATION],
      renderCell: ({ row }) => <OperationBadgeCell operation={row.operation} />,
      filterable: true,
      dataType: ColumnDataTypes.ENUM,
      filterOperators: ENUM_OPERATORS,
      statePersistence: {
        urlKey: AUDIT_LOGS_FILTER_URL_KEYS[FIELDS.OPERATION],
      },
      filterOptions: {
        type: FilterOptionTypes.STATIC,
        options: OPERATION_OPTIONS,
        render: (option, isSelected) => (
          <OperationFilterOption
            operation={option.value}
            isSelected={isSelected}
          />
        ),
      },
    },
    {
      id: FIELDS.RECORD_ID,
      header: 'Object ID',
      accessor: 'recordId',
      width: 300,
      sortable: true,
      sortField: FIELDS.RECORD_ID,
      sortKey: AUDIT_LOGS_FILTER_URL_KEYS[FIELDS.RECORD_ID],
      renderCell: ({ row }) => <ObjectIdCell recordId={row.recordId} />,
      filterable: true,
      dataType: ColumnDataTypes.TEXT,
      filterOperators: AUDIT_LOG_RECORD_ID_OPERATORS,
      statePersistence: {
        urlKey: AUDIT_LOGS_FILTER_URL_KEYS[FIELDS.RECORD_ID],
      },
    },
    {
      id: FIELDS.USER,
      header: 'User',
      accessor: 'userId',
      width: 220,
      sortable: true,
      sortField: FIELDS.USER,
      sortKey: AUDIT_LOGS_FILTER_URL_KEYS[FIELDS.USER],
      renderCell: ({ row }) => (
        <AuditLogUserCell
          userName={row.userName}
          userEmail={row.userEmail}
          metadata={row.metadata}
        />
      ),
      filterable: true,
      dataType: ColumnDataTypes.ENUM,
      filterOperators: ENUM_OPERATORS,
      statePersistence: { urlKey: AUDIT_LOGS_FILTER_URL_KEYS[FIELDS.USER] },
      filterOptions: {
        type: FilterOptionTypes.DYNAMIC,
        getQueryKey: params =>
          getAuditLogUserFilterOptionsQueryKey(organizationId, params),
        queryFn: params =>
          fetchAuditLogUserFilterOptions(organizationId, params),
        queryConfig: { staleTime: 2 * 60 * 1000 },
        dropdown: { width: 280 },
        pagination: { mode: 'offset', limit: 15 },
        render: (option, isSelected) => (
          <UserFilterOption
            userId={option.value}
            userName={(option.metadata?.name as string | null) ?? null}
            userEmail={(option.metadata?.email as string | null) ?? null}
            isSelected={isSelected}
          />
        ),
      },
    },
    {
      /*
       * [KIR-3550] Non-filterable "Source" column reads `metadata.source`
       * recorded by the audit trigger. Filtering/sorting would require backend
       * changes to the audit-logs table engine + a new filter-options endpoint,
       * so those are deferred; presentation alone unblocks the ticket ask.
       */
      id: 'source',
      header: 'Source',
      accessor: (row: AuditLog) => row.metadata,
      width: 120,
      renderCell: ({ row }) => <AuditSourceCell metadata={row.metadata} />,
    },
    {
      id: FIELDS.CHANGES,
      header: 'Changes',
      accessor: 'changedFields',
      minWidth: 400,
      sortable: true,
      sortField: FIELDS.CHANGES,
      sortKey: AUDIT_LOGS_FILTER_URL_KEYS[FIELDS.CHANGES],
      renderCell: ({ row }) => (
        <ChangedFieldsDisplay
          changedFields={row.changedFields}
          scopeGroupId={auditScopeGroupId(row)}
          groupNames={groupNames}
        />
      ),
      filterable: true,
      dataType: ColumnDataTypes.ENUM,
      filterOperators: ENUM_OPERATORS,
      statePersistence: { urlKey: AUDIT_LOGS_FILTER_URL_KEYS[FIELDS.CHANGES] },
      filterOptions: {
        type: FilterOptionTypes.DYNAMIC,
        getQueryKey: params =>
          getChangedFieldsFilterOptionsQueryKey(organizationId, params),
        queryFn: params =>
          fetchChangedFieldsFilterOptions(organizationId, params),
        queryConfig: { staleTime: 2 * 60 * 1000 },
        dropdown: { width: 320, align: 'center', collisionPadding: 8 },
        pagination: { mode: 'offset', limit: 20 },
        render: (option, isSelected) => (
          <ChangesFilterOption
            field={String(option.metadata?.field ?? '')}
            table={String(option.metadata?.table ?? '')}
            isSelected={isSelected}
          />
        ),
      },
    },
  ];
}
